import { Controller } from '@hotwired/stimulus';
import { Calendar } from 'vanilla-calendar-pro';

// Every popup a LIVE date-picker's calendar has built (registered by VanillaCalendar's
// `onInit`, which in input mode fires when the popup is created on first open).
// Module-level so all instances share it: `connect()` removes any body popup NOT in
// here, i.e. a popup that came back inside a restored Turbo snapshot and belongs to
// no calendar (see « Turbo cache » in the header). A WeakSet, so a removed popup is
// never kept alive by it.
const livePopups = new WeakSet();

/**
 * date-picker — drives a VanillaCalendar Pro v3 popup on the `calendar: true`
 * branch of `Form::DateInputComponent`.
 *
 * Two inputs back the control:
 *   • `display` — the VISIBLE nameless <input type="text"> the user reads/types,
 *     formatted FR dd/mm/yyyy. NEVER submitted (no `name`).
 *   • `hidden`  — the real Rails <input type="hidden"> carrying the ISO
 *     YYYY-MM-DD. The SOURCE OF TRUTH and the only field that POSTs.
 *
 * The hidden ISO field is authoritative on connect (a Turbo re-render that
 * repaints a fresh ISO is reflected back onto the FR display); picking a day or
 * typing a valid FR date writes the hidden field (and dispatches input/change so
 * sibling controllers react); typing garbage reverts the display to the last
 * valid value and never corrupts the hidden field (GOV.UK typed-fallback rule).
 *
 * A PROGRAMMATIC external write to the hidden field (a sibling controller / Turbo
 * Stream) re-seeds the display + the popup selection; the controller guards its
 * OWN writes (`_internalWrite`) so the hidden `change` listener never loops.
 *
 * All date math is split-parts / local-midnight (mirrors
 * `planning_phase_form_controller.js#parseDate/#formatDate`) — never
 * `new Date("yyyy-mm-dd")`, which JS parses as UTC and can drift a day.
 *
 * Targets:
 *   • display     — visible FR <input type="text"> (VanillaCalendar mounts here)
 *   • hidden      — real Rails <input type="hidden"> (ISO YYYY-MM-DD, submitted)
 *   • timeDisplay — OPT-IN: visible HH:MM <input type="text"> (VISIBLE AT REST,
 *                   not hidden behind the popup — RES-5). Editable directly.
 *   • timeHidden  — OPT-IN: real Rails <input type="hidden"> (HH:MM, submitted).
 *                   ALL time behavior is gated on `hasTimeHiddenTarget` — a
 *                   `date-picker` root with no `timeHidden` target behaves
 *                   EXACTLY as the date-only controller (the ~8 legacy callers).
 *
 * Values:
 *   • iso          (String) — initial ISO seed (fallback when the hidden field is empty)
 *   • locale       (String) — BCP-47 tag for the calendar (default 'fr-FR')
 *   • min          (String) — optional ISO floor (`displayDateMin`)
 *   • max          (String) — optional ISO ceiling (`displayDateMax`)
 *   • anchor       (String) — OPT-IN (F3): the ISO date whose MONTH the popup
 *                   OPENS on while the field is EMPTY. A LANDMARK, never a
 *                   proposed value — it never reaches `selectedDates`, so an
 *                   empty field STAYS empty after the calendar is opened. The
 *                   real ISO always wins (editing an existing record is
 *                   untouched), and a blank/malformed anchor falls back to
 *                   VanillaCalendar's native current-month default — neither the
 *                   server nor this controller ever fabricates "today".
 *   • time         (String) — OPT-IN: VanillaCalendar `selectionTimeMode`
 *                   ("24" | "12"); defaults to 24 when the `timeHidden` target
 *                   is present but the value is omitted.
 *   • selectedTime (String) — OPT-IN: initial HH:MM seed for the time half
 *                   (fallback when the `timeHidden` field is empty).
 *
 * Actions:
 *   none wired in markup — the controller binds its own listeners in connect():
 *   the VanillaCalendar `onChangeToInput` (+ opt-in `onChangeTime`) callbacks,
 *   a `blur` on `display` (+ opt-in `timeDisplay`), a `change` on `hidden`
 *   (+ opt-in `timeHidden`), a document-level `mousedown` (capture) guard, and
 *   the three document-level dismissal listeners below (`focusin`, `scroll`
 *   capture, `keydown` capture) and a document-level `turbo:before-cache` (see
 *   « Turbo cache »). Every document listener is removed in disconnect().
 *
 * Turbo cache (browser Back lost the fields, 2026-09-28):
 *   Turbo caches the page it leaves: it fires `turbo:before-cache`, waits one
 *   event-loop tick, then clones the OLD <body>. Rendering the new page during that
 *   tick detaches the old body, so Stimulus runs disconnect() on it BEFORE the
 *   clone: whatever disconnect() does to the DOM is what Back restores. The
 *   controller must therefore leave the markup exactly as the server rendered it.
 *   • VanillaCalendar's own `destroy()` does not. In input mode it removes
 *     `context.mainElement` from its parent, and until the popup has been opened
 *     once, `mainElement` IS the visible input: the input left the DOM, the cached
 *     page had no `display` target, and Back showed « Du » / « Au » with no field
 *     (« Missing target element "display" »). Once the popup exists, destroy()
 *     swaps the live input for the clone it took at init, which reverts every
 *     attribute set since (enabled / shown by `export-scale-period`), and it
 *     never calls hide(), so an open popup's document listeners stay behind.
 *     `#teardownCalendar()` is used instead and never calls destroy(): it closes the
 *     popup, removes the listeners VanillaCalendar put on the input (the cleanup
 *     `init()` returns) and removes the popup from <body>. The input is untouched.
 *   • `turbo:before-cache` runs the same teardown, for a clone taken while this
 *     controller is still connected: no popup travels into the cache. The page is
 *     normally left right after; if the element is still connected at the next
 *     `turbo:render` (a morph render keeps it), the calendar is rebuilt.
 *   • connect() is robust to a restored snapshot: it removes any body popup that
 *     no live calendar owns (`livePopups`), and returns without throwing when a
 *     target is missing, so one broken field never stops the page's other
 *     controllers from connecting.
 *
 * Dismissal (popup left open over the form, 2026-09-28):
 *   VanillaCalendar portals its popup to <body> and only closes it on a click
 *   outside or Escape. Two gaps followed: a keyboard exit (Tab / Shift+Tab to the
 *   next field) left it open, and when the field's scroll container (a modal
 *   body, a slide-over drawer) scrolled, the popup stayed where it was and ended
 *   up over other controls (the planning export modal: « Exporter » under a
 *   calendar day, a click changed « Au » instead of exporting). So:
 *   • `focusin` anywhere that is neither the `display` input nor inside THIS
 *     popup closes it. Focus moving INTO the popup (a month arrow, a day) keeps
 *     it open; a day pick still writes and closes through `#onPick`.
 *   • a `scroll` (capture, so element scrolls are seen too) that MOVES the field
 *     relative to its popup closes it. Closing rather than re-anchoring: a
 *     re-anchored popup would still float, unclipped, over the modal header or
 *     past the drawer edge once its field scrolls out of view. The test is the
 *     field/popup OFFSET recorded when the popup opens (`onShow`), not the mere
 *     occurrence of a scroll: a window scroll moves both together (the popup is
 *     absolutely positioned in page coordinates) and the browser's own
 *     scroll-into-view on focus fires its event after the popup opened. Neither
 *     detaches the popup, so neither closes it.
 *   • Escape with a calendar open closes ONLY the calendar. The export modal
 *     (`modal_controller.js`) and the slide-over (`slide_over_controller.js`)
 *     close on a `keydown` Escape heard on `document` in the BUBBLE phase, and so
 *     does VanillaCalendar's own Escape. A `document` CAPTURE listener runs before
 *     all three: it hides the calendar itself, then `preventDefault()` +
 *     `stopImmediatePropagation()`, so neither the modal (and the entries in it)
 *     nor the drawer closes. With no calendar open it returns at once and Escape
 *     behaves exactly as before (the next press closes the modal / drawer).
 *   • Enter in the field saves the typed date like a blur, before the implicit
 *     form submission it triggers, and closes the calendar (the same capture
 *     listener; the submission itself is left alone).
 *
 * Blur/click race guard (#613):
 *   VanillaCalendar's popup buttons steal focus on `mousedown`, which fires a
 *   SYNCHRONOUS `blur` on the display input BEFORE the `mouseup`/`click`. The blur
 *   handler used to call `calendar.set(...)`, which rebuilds the whole grid
 *   between the mousedown and the mouseup — detaching the button the user pressed,
 *   so the browser never synthesizes its `click` and the interaction is silently
 *   swallowed (flaky, both date-only and date+time instances). Guard: a
 *   document-level `mousedown` capture listener raises `#suppressBlurSync` when the
 *   press lands inside THIS instance's popup; `#onBlur`/`#onTimeBlur` then bail
 *   BEFORE any `calendar.set(...)` (the in-popup selection resolves via the
 *   `onChangeToInput`/`onChangeTime` callbacks instead). The flag is released on
 *   the next `mouseup` AND on the next tick (`setTimeout(0)`) so it can never
 *   stick — a stuck flag would permanently kill legitimate blur resync. A blur
 *   OUTSIDE the popup (click elsewhere, Tab) is untouched and resyncs as before.
 *
 * @example
 *   <div data-controller="date-picker"
 *        data-date-picker-iso-value="2026-06-25"
 *        data-date-picker-locale-value="fr-FR"
 *        data-date-picker-min-value="2026-01-01">
 *     <input type="text" data-date-picker-target="display" value="25/06/2026">
 *     <input type="hidden" name="phase[start_date]"
 *            data-date-picker-target="hidden" value="2026-06-25">
 *   </div>
 *
 * @example combined date+time (opt-in, RES-5)
 *   <div data-controller="date-picker"
 *        data-date-picker-iso-value="2026-06-25"
 *        data-date-picker-locale-value="fr-FR"
 *        data-date-picker-time-value="24"
 *        data-date-picker-selected-time-value="14:30">
 *     <input type="text" data-date-picker-target="display" value="25/06/2026">
 *     <input type="text" data-date-picker-target="timeDisplay" value="14:30">
 *     <input type="hidden" name="next_meeting[date]"
 *            data-date-picker-target="hidden" value="2026-06-25">
 *     <input type="hidden" name="next_meeting[time]"
 *            data-date-picker-target="timeHidden" value="14:30">
 *   </div>
 */
export default class extends Controller {
  static targets = ['display', 'hidden', 'timeDisplay', 'timeHidden'];
  static values = {
    iso: String,
    locale: String,
    min: String,
    max: String,
    // F3 — DECLARING it is what makes the server-rendered
    // `data-date-picker-anchor-value` do anything at all: Stimulus ignores an
    // UNDECLARED value silently (no error, no log), so the attribute shipped
    // with step 3.2a was inert until this line existed.
    anchor: String,
    time: String,
    selectedTime: String,
  };

  // Blur/click race guard (#613). Raised while a `mousedown` inside this
  // instance's popup is in flight so #onBlur/#onTimeBlur skip the grid-rebuilding
  // `calendar.set(...)`. PER INSTANCE (never shared): each date-picker owns its
  // own display input, so its own blur is the only one to suppress.
  #suppressBlurSync = false;

  connect() {
    // Re-init guard: Stimulus may reconnect a controller (Turbo morph, slot
    // re-render) without a full disconnect — never stack two calendars on one input.
    if (this.calendar) return;
    // A restored snapshot with a missing field (see « Turbo cache »): do nothing
    // rather than throw, disconnect() then has nothing to undo either.
    if (!this.hasDisplayTarget || !this.hasHiddenTarget) return;

    this.#sweepStalePopups();
    this.#buildCalendar();

    // The exact elements the listeners go on, so disconnect() removes them from
    // the same nodes (and knows whether connect() got this far).
    this.boundInputs = {
      display: this.displayTarget,
      hidden: this.hiddenTarget,
      timeDisplay: this.hasTimeHiddenTarget ? this.timeDisplayTarget : null,
      timeHidden: this.hasTimeHiddenTarget ? this.timeHiddenTarget : null,
    };

    this.onBlur = this.#onBlur.bind(this);
    this.onHiddenChange = this.#onHiddenChange.bind(this);
    this.boundInputs.display.addEventListener('blur', this.onBlur);
    this.boundInputs.hidden.addEventListener('change', this.onHiddenChange);

    // Blur/click race guard (#613): watch mousedowns in CAPTURE at the document
    // level (not on the popup node itself) — VanillaCalendar creates/repositions
    // its popup lazily on focus, so a listener bound to a snapshot node at connect
    // would go stale; the document listener resolves the live popup at event time.
    this.onDocumentMousedown = this.#onDocumentMousedown.bind(this);
    this.releaseBlurSuppression = this.#releaseBlurSuppression.bind(this);
    document.addEventListener('mousedown', this.onDocumentMousedown, true);

    // Dismissal (see header): focus leaving for anything outside the field and
    // its popup, or a scroll that detaches the popup from its field, closes it.
    this.onDocumentFocusin = this.#onDocumentFocusin.bind(this);
    this.onDocumentScroll = this.#onDocumentScroll.bind(this);
    document.addEventListener('focusin', this.onDocumentFocusin);
    document.addEventListener('scroll', this.onDocumentScroll, { capture: true, passive: true });
    // CAPTURE, so it runs before the modal / drawer / VanillaCalendar Escape
    // listeners, all on `document` in the bubble phase (see header).
    this.onDocumentKeydown = this.#onDocumentKeydown.bind(this);
    document.addEventListener('keydown', this.onDocumentKeydown, true);

    // Turbo cache (see header): no popup in the snapshot, input as rendered.
    this.onBeforeCache = this.#onBeforeCache.bind(this);
    this.onRenderAfterCache = this.#onRenderAfterCache.bind(this);
    document.addEventListener('turbo:before-cache', this.onBeforeCache);

    if (this.boundInputs.timeHidden) {
      this.onTimeBlur = this.#onTimeBlur.bind(this);
      this.onTimeHiddenChange = this.#onTimeHiddenChange.bind(this);
      this.boundInputs.timeDisplay.addEventListener('blur', this.onTimeBlur);
      this.boundInputs.timeHidden.addEventListener('change', this.onTimeHiddenChange);
    }
  }

  disconnect() {
    const inputs = this.boundInputs;
    if (!inputs) return; // connect() returned early: nothing was set up
    this.boundInputs = null;

    inputs.display.removeEventListener('blur', this.onBlur);
    inputs.hidden.removeEventListener('change', this.onHiddenChange);

    if (inputs.timeHidden) {
      inputs.timeDisplay.removeEventListener('blur', this.onTimeBlur);
      inputs.timeHidden.removeEventListener('change', this.onTimeHiddenChange);
    }

    // Blur/click race guard (#613): tear down the document listener + any pending
    // release (mouseup listener / next-tick timer) so nothing leaks across visits.
    document.removeEventListener('mousedown', this.onDocumentMousedown, true);
    this.#releaseBlurSuppression();

    document.removeEventListener('focusin', this.onDocumentFocusin);
    document.removeEventListener('scroll', this.onDocumentScroll, { capture: true });
    document.removeEventListener('keydown', this.onDocumentKeydown, true);
    document.removeEventListener('turbo:before-cache', this.onBeforeCache);
    document.removeEventListener('turbo:render', this.onRenderAfterCache);

    // NEVER `this.calendar.destroy()`: it removes the input or swaps it for a stale
    // clone, and Turbo caches the result (see « Turbo cache » in the header).
    this.#teardownCalendar(inputs.display);
  }

  // Builds the VanillaCalendar instance on the display input, from the hidden
  // ISO (the source of truth). Used by connect() and by the rebuild after a
  // `turbo:before-cache` teardown the element survived.
  #buildCalendar() {
    // Source of truth is the hidden ISO; fall back to the seed value. Re-seed the
    // FR display so a Turbo re-render that repainted a fresh ISO is reflected on
    // reconnect (the server already wrote the matching FR value, but a partial
    // re-render that only swapped the hidden field would leave the display stale).
    const iso = this.hiddenTarget.value || this.isoValue || '';
    this.displayTarget.value = this.isoToFr(iso);

    // Opt-in combined date+time (RES-5): the time is VISIBLE AT REST, never
    // hidden behind the popup. Source of truth mirrors the date pair — the
    // hidden `timeHidden` field wins on reconnect, the seeded value falls back.
    let seededTime = '';
    if (this.hasTimeHiddenTarget) {
      seededTime = this.timeHiddenTarget.value || this.selectedTimeValue || '';
      this.timeDisplayTarget.value = seededTime;
    }

    const options = {
      inputMode: true,
      enableWeekNumbers: true,
      firstWeekday: 1,
      locale: this.localeValue || 'fr-FR',
      positionToInput: 'auto',
      // Jerico is a light-only app. Pin the calendar to the light theme and
      // DISABLE VanillaCalendar's OS/attribute theme auto-detection — otherwise a
      // dark-mode OS yields data-vc-theme="dark", which matches NONE of our
      // light-scoped theme CSS (only layout.css + themes/light.css are imported),
      // leaving the popup transparent/unstyled (it floats over the form unseen).
      selectedTheme: 'light',
      themeAttrDetect: false,
      selectedDates: iso ? [iso] : [],
      // Open the popup on the SELECTED date's month/year, not the current month
      // (#619 T9 secondary). `enableJumpToSelectedDate` is the library's declarative
      // baseline (derives month/year from selectedDates at init when neither is set);
      // the explicit selectedMonth/selectedYear below make it deterministic and also
      // drive every later resync, where the library would otherwise reuse a stale
      // month. A blank field seeds nothing → the ANCHOR's month (F3) when one is
      // given, else the current-month default.
      enableJumpToSelectedDate: true,
      onChangeToInput: (self) => this.#onPick(self),
      // Dismissal: record where the popup sits relative to its field once
      // VanillaCalendar has positioned it (show() positions BEFORE onShow).
      onShow: () => this.#rememberPopupOffset(),
    };
    // ONLY the month/year is derived here. `selectedDates` above stays bound to
    // `iso` — the anchor is deliberately absent from it, which is what keeps an
    // empty field EMPTY once the calendar has been opened (F3).
    Object.assign(options, this.openingMonthYear(iso) || {});
    if (this.hasMinValue && this.minValue) options.displayDateMin = this.minValue;
    if (this.hasMaxValue && this.maxValue) options.displayDateMax = this.maxValue;

    // Opt-in combined date+time (RES-5) — gated on `hasTimeHiddenTarget` so a
    // date-only `date-picker` root never touches VanillaCalendar's time API.
    if (this.hasTimeHiddenTarget) {
      options.selectionTimeMode = this.hasTimeValue && this.timeValue ? Number(this.timeValue) : 24;
      if (seededTime) options.selectedTime = seededTime;
      options.onChangeTime = (self, _event, isError) => this.#onChangeTime(self, isError);
    }

    // Record every popup this calendar builds (in input mode VanillaCalendar
    // builds it on first open, then calls `onInit`), so connect() can tell a live
    // popup from one that came back inside a restored snapshot.
    options.onInit = (self) => livePopups.add(self.context.mainElement);

    this.calendar = new Calendar(this.displayTarget, options);
    // In input mode `init()` only puts click / focus / keydown listeners on the
    // input and returns their cleanup: `#teardownCalendar()` calls it.
    this.calendarInputCleanup = this.calendar.init();
  }

  // Undoes `#buildCalendar()` WITHOUT `calendar.destroy()` (see « Turbo cache » in
  // the header): the input stays the very node the server rendered.
  //   1. hide() an open popup — the only place VanillaCalendar removes the
  //      document `keydown` / `click` and window `resize` listeners show() added;
  //   2. remove its click / focus / keydown listeners from the input;
  //   3. remove its popup from <body> (absent while never opened: `mainElement`
  //      is then the input itself, which must stay).
  #teardownCalendar(input) {
    const calendar = this.calendar;
    this.calendar = null;
    this.popupOffset = null;
    if (!calendar) return;

    if (calendar.context?.isShowInInputMode) calendar.hide();
    this.calendarInputCleanup?.();
    this.calendarInputCleanup = null;

    // Not gated on `isConnected`: disconnect() usually runs on the DETACHED old
    // body Turbo is about to clone, where a popup is no longer "connected" but
    // would still be cached.
    const popup = calendar.context?.mainElement;
    if (popup && popup !== input) popup.remove();
  }

  // `turbo:before-cache`: the page is about to be cloned into Turbo's cache.
  #onBeforeCache() {
    this.#teardownCalendar(this.boundInputs?.display);
    // The visit normally replaces this page, and disconnect() drops this listener.
    // Still connected at the next render ⇒ the element survived it: rebuild.
    document.addEventListener('turbo:render', this.onRenderAfterCache, { once: true });
  }

  #onRenderAfterCache() {
    if (this.calendar || !this.boundInputs || !this.element.isConnected) return;
    this.#buildCalendar();
  }

  // A body popup no live calendar owns can only come from a restored snapshot
  // cached while a popup existed: remove it, so a reopened calendar is never
  // shown next to a dead copy. Popups of other live date-pickers are kept.
  #sweepStalePopups() {
    for (const node of document.body.querySelectorAll(':scope > .vc[data-vc-input]')) {
      if (!livePopups.has(node)) node.remove();
    }
  }

  // ── Anchor (F3) ─────────────────────────────────────────────────────────────

  // Stimulus value callback — fired when a SIBLING controller re-writes
  // `data-date-picker-anchor-value` mid-form (`planning-phase-form` on a « Lot
  // parent » change / on the start date being typed).
  //
  // ⚠️ This callback is NOT optional: `connect()` builds the whole calendar in one
  // shot and this controller carries no other `*ValueChanged`. Without it a later
  // anchor write would land in the DOM, be parsed by Stimulus, and change NOTHING
  // on screen — the re-anchoring feature would be silently dead code.
  //
  // Two guards, in order:
  //   1. `!this.calendar` — Stimulus fires every declared value's changed callback
  //      ONCE at start-up, BEFORE `controller.connect()` runs (the value observer
  //      starts first). There is no calendar to move yet, and `connect()` reads
  //      `anchorValue` itself, so the first call must be a no-op rather than a crash.
  //   2. a filled hidden field — the real ISO ALWAYS wins over a landmark, so
  //      re-anchoring can never drag an existing record's popup off its own date.
  // Nothing here touches `selectedDates`: re-anchoring moves the visible month and
  // NEVER selects, so it can never rewrite a date.
  anchorValueChanged() {
    if (!this.calendar) return;
    if (this.hiddenTarget.value) return;

    const jump = this.#monthYearFromIso(this.anchorValue);
    if (jump) this.calendar.set(jump);
  }

  // The month/year the popup opens on: the field's own ISO when it holds one,
  // else the ANCHOR's month (F3), else null → VanillaCalendar's native
  // current-month default (we never fabricate "today" client-side either).
  // Returns ONLY `{ selectedMonth, selectedYear }` — by construction it cannot
  // carry a selection, which is the structural guarantee behind « un champ vide
  // reste vide ».
  openingMonthYear(iso) {
    return this.#monthYearFromIso(iso) || this.#monthYearFromIso(this.anchorValue);
  }

  // VanillaCalendar's onChangeToInput callback. v3 exposes the selection as an
  // array of ISO YYYY-MM-DD strings on `self.context.selectedDates`; element [0]
  // is the chosen day (empty when the user cleared it).
  #onPick(self) {
    const picked = self.context.selectedDates[0];

    if (picked) {
      // A sibling lock-step controller may snap the hidden field during #writeHidden;
      // reflect the FINAL hidden value (for non-lock-step fields final === picked).
      this.#writeHidden(picked);
      const final = this.hiddenTarget.value;
      this.displayTarget.value = this.isoToFr(final);
      // Always resync selection + month/year so a REOPEN lands on the picked month
      // (#619) — the library does not persist in-popup navigation into selectedMonth.
      this.#syncCalendarToIso(final);
      self.hide();
    } else {
      this.displayTarget.value = '';
      this.#writeHidden('');
    }
  }

  // Typed FR fallback (GOV.UK rule): a user may type dd/mm/yyyy directly. On blur,
  // STRICT-parse the display value.
  //   • valid          → write hidden (ISO), normalize the display, sync the popup.
  //   • invalid + text → REVERT display to the last valid value (or ''), never
  //                      corrupt the hidden field.
  //   • empty          → clear the hidden field.
  #onBlur(event) {
    // Blur/click race guard (#613): the blur was caused by a mousedown INSIDE the
    // popup — do NOT `calendar.set(...)` (it rebuilds the grid mid-click and eats
    // the interaction). The pick resolves via the `onChangeToInput` callback; an
    // in-popup navigation (arrows, month/year) leaves the text/hidden untouched.
    if (this.#suppressBlurSync) return;
    this.#closeUnlessFocusInPopup(event);

    const raw = this.displayTarget.value.trim();

    if (raw === '') {
      this.#writeHidden('');
      return;
    }

    const iso = this.frToIso(raw);
    if (iso && this.#withinBounds(iso)) {
      // A sibling lock-step controller may snap the hidden field during #writeHidden;
      // reflect the FINAL hidden value (for non-lock-step fields final === iso).
      this.#writeHidden(iso);
      const final = this.hiddenTarget.value;
      this.displayTarget.value = this.isoToFr(final);
      // Resync selection + month/year so a REOPEN after a typed date lands on that
      // date's month (#619), not the current month.
      this.#syncCalendarToIso(final);
    } else {
      // Revert to the last valid hidden value (FR), or blank when none.
      this.displayTarget.value = this.isoToFr(this.hiddenTarget.value);
    }
  }

  // A PROGRAMMATIC external write to the hidden field (sibling controller / Turbo)
  // — NOT one of our own #writeHidden dispatches (guarded by _internalWrite).
  // Re-seed the FR display + the popup selection from the new ISO value.
  #onHiddenChange() {
    if (this._internalWrite) return;

    const iso = this.hiddenTarget.value || '';
    this.displayTarget.value = this.isoToFr(iso);
    // Resync selection + month/year so a REOPEN after a sibling/Turbo write lands on
    // the new date's month (#619). A blank ISO clears selection and keeps the current
    // month (unchanged behavior).
    this.#syncCalendarToIso(iso);
  }

  // Single chokepoint for writing the hidden ISO field. Dispatches `input` AND
  // `change` (bubbling) so listeners on the form / sibling controllers react,
  // while `_internalWrite` short-circuits our own `change` listener to avoid a
  // feedback loop.
  #writeHidden(iso) {
    this.hiddenTarget.value = iso;

    this._internalWrite = true;
    this.hiddenTarget.dispatchEvent(new Event('input', { bubbles: true }));
    this.hiddenTarget.dispatchEvent(new Event('change', { bubbles: true }));
    this._internalWrite = false;
  }

  // ── Time (opt-in, RES-5) ─────────────────────────────────────────────────

  // VanillaCalendar's onChangeTime callback. v3 reports the picked time on
  // `self.context.selectedTime` ("HH:MM"); `isError` is set when the library
  // rejects an in-popup edit — bail without touching either field.
  #onChangeTime(self, isError) {
    if (isError) return;

    const time = self.context.selectedTime;
    if (!time) return;

    // Mirrors #onPick's "final" read: reflect whatever the hidden field ends
    // up holding after the write (defensive against a future sibling
    // controller reacting to the dispatched change).
    this.#writeTimeHidden(time);
    const final = this.timeHiddenTarget.value;
    this.timeDisplayTarget.value = final;
  }

  // Typed HH:MM fallback: the visible time display is directly editable (RES-5
  // — "visible at rest" implies "editable at rest"). On blur, STRICT-parse it.
  //   • valid          → write hidden, normalize the display, sync the popup seed.
  //   • invalid + text → REVERT display to the last valid hidden value (or ''),
  //                      never corrupt the hidden field.
  //   • empty          → clear the hidden field.
  #onTimeBlur() {
    // Blur/click race guard (#613): same as #onBlur — a mousedown inside the popup
    // (incl. its time controls) resolves via `onChangeTime`; never `calendar.set`.
    if (this.#suppressBlurSync) return;

    const raw = this.timeDisplayTarget.value.trim();

    if (raw === '') {
      this.#writeTimeHidden('');
      return;
    }

    if (this.#isValidTime(raw)) {
      this.#writeTimeHidden(raw);
      const final = this.timeHiddenTarget.value;
      this.timeDisplayTarget.value = final;
      this.calendar?.set({ selectedTime: final });
    } else {
      // Revert to the last valid hidden value, or blank when none.
      this.timeDisplayTarget.value = this.timeHiddenTarget.value || '';
    }
  }

  // A PROGRAMMATIC external write to the hidden time field (sibling controller
  // / Turbo) — NOT one of our own #writeTimeHidden dispatches (guarded by
  // _internalTimeWrite). Re-seed the visible display + the popup selection.
  #onTimeHiddenChange() {
    if (this._internalTimeWrite) return;

    const time = this.timeHiddenTarget.value || '';
    this.timeDisplayTarget.value = time;
    if (time) this.calendar?.set({ selectedTime: time });
  }

  // Single chokepoint for writing the hidden time field. Mirrors #writeHidden:
  // dispatches `input`/`change` (bubbling) for sibling controllers (e.g.
  // `cr-inline-field#patchField`), guarded by `_internalTimeWrite` so our own
  // `change` listener never loops.
  #writeTimeHidden(time) {
    this.timeHiddenTarget.value = time;

    this._internalTimeWrite = true;
    this.timeHiddenTarget.dispatchEvent(new Event('input', { bubbles: true }));
    this.timeHiddenTarget.dispatchEvent(new Event('change', { bubbles: true }));
    this._internalTimeWrite = false;
  }

  // STRICT HH:MM (00-23:00-59) — mirrors #isRealDate's rejection of garbage.
  #isValidTime(value) {
    return /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
  }

  // ── Blur/click race guard (#613) ─────────────────────────────────────────────

  // Document-level `mousedown` (capture) handler. Raise the suppression flag ONLY
  // when the press lands inside THIS instance's popup, then schedule a guaranteed
  // release. A press elsewhere (or in another date-picker's popup) is ignored, so
  // a legitimate blur (click outside, Tab) still resyncs normally.
  #onDocumentMousedown(event) {
    if (!this.#popupContains(event.target)) return;

    this.#suppressBlurSync = true;
    this.#scheduleBlurSuppressionRelease();
  }

  // True when `target` sits inside THIS calendar's popup, read from VanillaCalendar's
  // live `context.mainElement` (per-instance precise). With no calendar (torn down
  // for the Turbo cache) nothing is inside. Never a `.vc` class match: that was NOT
  // instance-scoped, a press inside ANOTHER date-picker's popup matched it and
  // wrongly suppressed this field's blur-sync (#619 F5). (The former fallback, a
  // pre/post-`init()` diff of body popups, was always empty — in input mode the
  // popup is only built on first open — and went with the Turbo cache fix.)
  #popupContains(target) {
    if (!target) return false;

    const popup = this.calendar?.context?.mainElement;
    return popup ? popup.contains(target) : false;
  }

  // Belt-and-suspenders release: the flag only needs to survive the SYNCHRONOUS
  // blur that fires during the mousedown, but it must NEVER stick (a stuck flag
  // permanently kills legitimate blur resync). Release on the next `mouseup` AND
  // on the next tick — whichever fires first; a double release is a no-op.
  #scheduleBlurSuppressionRelease() {
    document.addEventListener('mouseup', this.releaseBlurSuppression, {
      once: true,
      capture: true,
    });
    clearTimeout(this.blurSuppressionTimer);
    this.blurSuppressionTimer = setTimeout(this.releaseBlurSuppression, 0);
  }

  #releaseBlurSuppression() {
    this.#suppressBlurSync = false;
    clearTimeout(this.blurSuppressionTimer);
    document.removeEventListener('mouseup', this.releaseBlurSuppression, { capture: true });
  }

  // ── Dismissal (focus leaves, scroll detaches) ───────────────────────────────

  // VanillaCalendar's own open flag (set in show(), cleared in hide()).
  #isOpen() {
    return Boolean(this.calendar?.context?.isShowInInputMode);
  }

  // The field's own `blur` (not the Enter path, which passes no event): focus
  // leaving for NOTHING or for anything outside THIS popup closes it. `focusin`
  // covers a move to another control, but a container that hides the focused
  // field (a drawer closing after a submit, which first hands focus back to the
  // field — VanillaCalendar reopens on `focus` — then hides it) sends focus to
  // <body> and fires no `focusin` at all: the popup stayed over the page (QA
  // 2026-09-28). A blur INTO the popup (VanillaCalendar's Tab onto a day) keeps it.
  #closeUnlessFocusInPopup(event) {
    if (!event || !this.#isOpen()) return;
    if (event.relatedTarget && this.#popupContains(event.relatedTarget)) return;

    this.calendar.hide();
  }

  // Document `focusin`: Tab / Shift+Tab / a click on another focusable control.
  // Focus staying on the field or entering THIS popup (arrows, days) keeps it
  // open; anything else closes it. The typed-date write is untouched: it runs on
  // the field's own `blur`, which precedes this `focusin`.
  #onDocumentFocusin(event) {
    if (!this.#isOpen()) return;

    const target = event.target;
    if (target === this.displayTarget || this.#popupContains(target)) return;

    this.calendar.hide();
  }

  // Document `scroll` (capture): closes the popup once its field has moved
  // relative to it (a scroll container of the field scrolled). A scroll that
  // moves both together (the window) leaves it open — see the header.
  #onDocumentScroll(event) {
    if (!this.#isOpen()) return;
    if (this.#popupContains(event.target)) return;

    const opened = this.popupOffset;
    const current = this.#popupOffset();
    if (!opened || !current) return;

    const moved =
      Math.abs(current.top - opened.top) > 1 || Math.abs(current.left - opened.left) > 1;
    if (moved) this.calendar.hide();
  }

  // Document `keydown` (capture): Escape with THIS calendar open closes the
  // calendar only. The event is stopped here, so VanillaCalendar's own Escape
  // listener never runs either — hence the explicit hide(). `preventDefault()`
  // also marks it consumed for `slide_over_controller`'s `defaultPrevented` guard.
  //
  // Enter in THIS field saves the typed date exactly as a blur would, BEFORE the
  // browser's implicit form submission (the default action of this keydown):
  // otherwise the form posted the OLD hidden date, the drawer closed as on a
  // success, and nothing had changed (QA 2026-09-28). The submission goes on
  // untouched; the calendar closes so it does not stay over the page.
  #onDocumentKeydown(event) {
    if (event.key === 'Enter' && event.target === this.displayTarget) {
      this.#onBlur();
      if (this.#isOpen()) this.calendar.hide();
      return;
    }
    if (event.key !== 'Escape') return;
    if (!this.#isOpen()) return;

    this.calendar.hide();
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  #rememberPopupOffset() {
    this.popupOffset = this.#popupOffset();
  }

  // Popup position relative to its field, in viewport pixels; null while the
  // popup does not exist yet (in inputMode `mainElement` is the input itself
  // until the first open builds the popup).
  #popupOffset() {
    const popup = this.calendar?.context?.mainElement;
    if (!popup || popup === this.displayTarget) return null;

    const field = this.displayTarget.getBoundingClientRect();
    const box = popup.getBoundingClientRect();
    return { top: box.top - field.top, left: box.left - field.left };
  }

  // ── Date helpers (TZ-safe, split-parts) ─────────────────────────────────────

  // 'YYYY-MM-DD' → 'dd/mm/yyyy'. '' on blank / malformed / non-real date. Built
  // from split int parts and round-trip-validated — never `new Date(string)`.
  isoToFr(value) {
    if (!value) return '';

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return '';

    const [, y, m, d] = match;
    const year = parseInt(y, 10);
    const month = parseInt(m, 10);
    const day = parseInt(d, 10);
    if (!this.#isRealDate(year, month, day)) return '';

    return `${d}/${m}/${y}`;
  }

  // 'dd/mm/yyyy' → 'YYYY-MM-DD' | null. STRICT: requires the dd/mm/yyyy shape AND
  // a real calendar date (rejects 31/02/2026, 00/00/0000, etc.).
  frToIso(value) {
    if (!value) return null;

    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
    if (!match) return null;

    const [, d, m, y] = match;
    const day = parseInt(d, 10);
    const month = parseInt(m, 10);
    const year = parseInt(y, 10);
    if (!this.#isRealDate(year, month, day)) return null;

    return `${y}-${m}-${d}`;
  }

  // Resync the popup to an ISO date: its selection AND its displayed month/year, so
  // reopening the calendar lands on that date's month rather than the current month
  // (#619). A blank ISO clears the selection and falls back to the ANCHOR's month
  // (F3) — that is the path a sibling takes when it BLANKS the field (e.g.
  // `planning-phase-form#recomputeEnd` clearing « Fin »): the emptied calendar must
  // land back on the useful period, not on the current month. With no anchor the
  // behavior is byte-for-byte the previous one (no month/year passed).
  #syncCalendarToIso(iso) {
    const selectedDates = iso ? [iso] : [];
    const jump = this.openingMonthYear(iso);
    this.calendar?.set(jump ? { selectedDates, ...jump } : { selectedDates });
  }

  // Derive VanillaCalendar's { selectedMonth, selectedYear } from an ISO date, or
  // null for a blank/malformed one. VanillaCalendar v3 `selectedMonth` is 0-BASED
  // (native Date.getMonth()), while the ISO 'MM' is 1-based — subtract one.
  #monthYearFromIso(iso) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
    if (!match) return null;

    const [, year, month] = match;
    return { selectedMonth: parseInt(month, 10) - 1, selectedYear: parseInt(year, 10) };
  }

  // Typed/seeded ISO within the optional [min,max] picker bounds. Lexical compare
  // is chronological for YYYY-MM-DD.
  #withinBounds(iso) {
    if (this.hasMinValue && this.minValue && iso < this.minValue) return false;
    if (this.hasMaxValue && this.maxValue && iso > this.maxValue) return false;
    return true;
  }

  // Validate split int parts form a real date by round-tripping through a local
  // Date (local-midnight, never UTC) and checking the parts come back unchanged.
  #isRealDate(year, month, day) {
    if (!year || !month || !day) return false;
    if (month < 1 || month > 12 || day < 1 || day > 31) return false;

    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  }
}
